<script lang="ts">
  import { onMount } from "svelte";
  import Fooji from "@fooji/microsites";
  import { copy } from "../lib/stores/copy";
  import { storeSelector } from "../lib/helpers/countryStore";
  import EmptyBox from "../assets/prizeBox/empty-box.webp";
  import BoxLight from "../assets/prizeBox/box-light.webp";
  import OneHour from "../assets/prizeBox/prizes/one-hour.webp";
  import BattlePass from "../assets/prizeBox/prizes/battle-pass.webp";
  import PrizePoints500 from "../assets/prizeBox/prizes/prize-points-500.webp";
  import PrizePoints1100 from "../assets/prizeBox/prizes/prize-points-1.webp";
  import PrizePoints2400 from "../assets/prizeBox/prizes/prize-points-2.webp";
  import PrizePoints13000 from "../assets/prizeBox/prizes/prize-points-13.webp";
  import PrizeOperator from "../assets/prizeBox/prizes/prize-operator.webp";
  import PrizeCharm from "../assets/prizeBox/prizes/prize-charm.webp";
  import PrizeCallingCard from "../assets/prizeBox/prizes/prize-card.webp";
  import PrizeCallingCardAndCharm from "../assets/prizeBox/prizes/prize-callingcardandcharm-intl.webp";
  import PrizeEmblemIntl from "../assets/prizeBox/prizes/prize-emblem-intl.webp";
  import PrizeOperatorIntl from "../assets/prizeBox/prizes/prize-operator-intl.webp";
  import PrizeEmblem from "../assets/prizeBox/prizes/prize-emblem.webp";
  import { notify } from "../lib/helpers/bugsnag";
  import { isDemo } from "../lib/stores/mode";
  import { setPage, setFlowStep, setLoading } from "../lib/stores/route";
  import { countryStore } from "../lib/stores/country";
  import { isPrimaryCampaign } from "../lib/constants/countries";

  let isBoxOpen = false;
  let prizeAsset: string | undefined; // TODO: If we're to always show the prize, this should not be undefined
  let isOperatorSkin = false;

  const prizeMap: Record<string, string> = isPrimaryCampaign
    ? {
      // Prizes block
      "battlepassplus15minsdualxp": BattlePass,
      "500callofdutypointsplus15minsdualxp": PrizePoints500,
      "1100callofdutypointsplus15minsdualxp": PrizePoints1100,
      "2400callofdutypointsplus15minsdualxp": PrizePoints2400,
      "13000callofdutypointsplus15minsdualxp": PrizePoints13000,
      // Rewards block
      "1hourdual2xp1": OneHour,
      "1hourdual2xp2": OneHour,
      "1hourdual2xp3": OneHour,
      "1hourdual2xp4": OneHour,
      "1hourdual2xp5": OneHour,
      "callingcard": PrizeCallingCard,
      "emblem": isPrimaryCampaign ? PrizeEmblem : PrizeEmblemIntl,
      "charm": PrizeCharm,
      "operatorskin1": isPrimaryCampaign ? PrizeOperator : PrizeOperatorIntl,
      "operatorskin2": isPrimaryCampaign ? PrizeOperator : PrizeOperatorIntl,
    }
    : {
      "callingcardandcharm": PrizeCallingCardAndCharm,
      "emblem": isPrimaryCampaign ? PrizeEmblem : PrizeEmblemIntl,
      "operatorskin1": isPrimaryCampaign ? PrizeOperator : PrizeOperatorIntl,
      "operatorskin2": isPrimaryCampaign ? PrizeOperator : PrizeOperatorIntl,
    }

  class ErrorWithCampaignUser extends Error {
    campaignUser: unknown;

    constructor(message: string, campaignUser: unknown) {
      super(message);
      this.campaignUser = campaignUser;
    }
  }

  const enter = async (firstAttempt = true) => {
    const { currentPhase, campaignUser } = await Fooji.Campaign.enter(undefined as unknown as number);
    
    if (currentPhase.startsWith("thank-you")) {
      const lastWonItems = (campaignUser as unknown as { last_won_items?: { name_key: string }[] }).last_won_items;
      const prizeKey: string = lastWonItems?.[0]?.name_key!;
      prizeAsset = prizeMap[prizeKey]; // TODO: should we default to something?

      if (!prizeKey) {
        const errorMessage = "No valid prize key on thank you phase: " + prizeKey;
        notify({ error: new Error(errorMessage), errorMessage, additionalMetadata: { campaignUser } });
      }

      if (!prizeAsset) {
        const errorMessage = "No valid prize asset on thank you phase for prize key: " + prizeKey;
        notify({ error: new Error(errorMessage), errorMessage, additionalMetadata: { campaignUser } });
      }

      isOperatorSkin = prizeKey?.startsWith("operatorskin")

      return setLoading(false);
    }

    if (currentPhase.startsWith("complete-order") && firstAttempt) {
      return enter(false);
    }

    throw new ErrorWithCampaignUser("Unexpected phase: " + currentPhase, campaignUser);
  }

  type CampaignUser = {
    last_entry_validation_error?: string[];
    status_reason?: string;
  }

  onMount(async () => {
    if (isDemo()) {
      const query = new URLSearchParams(document.location.search);
      const prizeKeys = Object.keys(prizeMap);

      const prizeKey = prizeMap[query.get("prize") as string]
        ? query.get("prize") as string
        : prizeKeys[Math.floor(Math.random() * prizeKeys.length)]

      prizeAsset = prizeMap[prizeKey];
      isOperatorSkin = prizeKey.startsWith("operatorskin");
      setLoading(false);
    } else {
      try {
        setLoading(true);
        await enter();
      } catch (error) {
        const errorMessage = (error as Error)?.message
        const campaignUser = (error as unknown as { campaignUser?: CampaignUser }).campaignUser;

        // COD_INVALID_COUNTRY_FOR_RECEIPT_VALIDATION - When country is not in the valid country map (CA, MX)
        // COD_CODE_INVALID - Invalid COD code
        // COD_RECEIPT_INVALID - Invalid COD receipt
        // COD_CODE_TRACKING_ID_MISSING - Code validation successful but no tracking ID
        // COD_RECEIPT_TRACKING_ID_MISSING - Receipt validation successful but no tracking ID
        // COD_CODE_VALIDATION_UNAVAILABLE - Unable to validate COD code
        // COD_RECEIPT_VALIDATION_UNAVAILABLE - Unable to validate COD receipt
        // COD_CODE_AND_RECEIPT_CONFLICT - Both code and receipt provided (mutually exclusive)
        // COD_CODE_AND_RECEIPT_MISSING - No code or receipt provided
        // COD_USER_IDENTITY_NOT_FOUND - User identity not found for callofduty application
        // COD_COUNTRY_NOT_FOUND - COD country not found in user metadata
        // COD_VALIDATE_CODE_INVALID_PARAMETERS - Invalid COD code parameters (400 error)
        // COD_VALIDATE_RECEIPT_INVALID_PARAMETERS - Invalid COD receipt parameters (400 error)
        // COD_SERVICE_UNAVAILABLE - COD validation service temporarily unavailable
        // NO_COD_CODE - Little Caesars COD code is missing
        // LOCATION_NOT_FOUND - Location with ID not found
        // LOCATIONS_DATA_NOT_FOUND - Locations data not found in S3
        // LOCATION_FETCH_ERROR - Failed to fetch location data
        // NO_COD_RECEIPT_DATA - Little Caesars COD receipt data is missing
        // COD_RECEIPT_NOT_AVAILABLE_FOR_US - COD receipt not available for US
        // COD_RECEIPT_VALIDATION_UNAVAILABLE_FOR_LOCATION - COD receipt validation not available for this location
        // NO_IMAGE_UPLOADED - No image uploaded
        // IMAGE_VALIDATION_FAILED - Image validation failed
        if (errorMessage?.includes("Unlucky")) { // TODO: this might be replaceable with the status reason as well...
          setPage("error", "unlucky");
        } else if (campaignUser?.status_reason === "invalid-entry") {
          const validationErrors = campaignUser?.last_entry_validation_error || [];
          
          if (
            validationErrors.includes("COD_RECEIPT_INVALID") ||
            validationErrors.includes("IMAGE_VALIDATION_FAILED") ||
            validationErrors.includes("COD_CODE_INVALID") ||
            validationErrors.includes("COD_INVALID_COUNTRY_FOR_RECEIPT_VALIDATION") ||
            validationErrors.includes("COD_RECEIPT_VALIDATION_UNAVAILABLE_FOR_LOCATION")
          ) {
            setPage("error", "invalidEntry", () => setFlowStep("code"));
          } else if (validationErrors.includes("COD_RECEIPT_VALIDATION_UNAVAILABLE")) {
            notify({ error, errorMessage: "Receipt validation unavailable error: " + validationErrors.join(", "), additionalMetadata: { campaignUser } });
            setPage("error", "unableToValidateReceipt", () => setFlowStep("code"));
          } else if (validationErrors.includes("COD_SERVICE_UNAVAILABLE")) {
            // Because reused codes are currenlty throwing 500
            notify({ error, errorMessage: "Receipt validation unavailable error: " + validationErrors.join(", "), additionalMetadata: { campaignUser } });
            setPage("error", "unableToValidateReceipt", () => setFlowStep("code"));
          } else {
            // Check if we should notify bugsnag for specific errors (they are service errors, all others are user errors)
            const serviceIssuesErrors = [
              "COD_CODE_VALIDATION_UNAVAILABLE",
              "COD_RECEIPT_VALIDATION_UNAVAILABLE",
              "COD_SERVICE_UNAVAILABLE",
              "LOCATIONS_DATA_NOT_FOUND",
              "LOCATION_FETCH_ERROR",
            ];

            const weirdErrors = [
              "COD_CODE_TRACKING_ID_MISSING",
              "COD_RECEIPT_TRACKING_ID_MISSING",
              "COD_USER_IDENTITY_NOT_FOUND",
              "COD_VALIDATE_CODE_INVALID_PARAMETERS",
              "COD_VALIDATE_RECEIPT_INVALID_PARAMETERS"
            ];
            
            const shouldNotify = validationErrors.some(error => serviceIssuesErrors.includes(error) || weirdErrors.includes(error));
            
            if (shouldNotify) {
              notify({ error, errorMessage: "Entry validation failed with errors: " + validationErrors.join(", "), additionalMetadata: { campaignUser } });
              console.error(error);
            }

            setPage("error");
          }
        } else {
          if (errorMessage.includes("duplicate-order")) {
            setPage("error", "alreadyParticipatedToday");
          } else {
            notify({ error, errorMessage: "Campaign enter failed", additionalMetadata: { campaignUser } });
            console.error(error);
            setPage("error");
          }
        }

        return setLoading(false);
      }
    }

    setTimeout(() => { isBoxOpen = true }, 2000);
  });
</script>

<main class="relative flex bg-dark bg-cover bg-no-repeat bg-[center_top] bg-[url('/bg/mobile-bg6.webp')] tabletLg:bg-[url('/bg/main-bg.webp')] tabletLg:pb-0">
  <div class="relative flex items-center flex-col px-[16px] text-center grow tabletLg:flex-none z-[1] tabletLg:w-[72vw]">
    <div class="w-full tablet:w-auto bg-black/50 rounded-md my-4 p-4 mb-[calc(118px+1rem)] laptop:mb-4">
      <h1 class="font-MrEavesXLSanNarOTUltra flex flex-col items-center text-primary whitespace-normal leading-[0.75] uppercase mb-[14px] text-[56px] tablet:block laptop:mb-[20px] mobileS:text-[72px] mobile:text-[84px] tablet:text-[84px] laptop:text-[100px] desktop:text-[110px] desktopLg:text-[140px] tablet:whitespace-nowrap">
        {@html $copy.thankYou.title}
      </h1>
      <h2 class="font-MrEavesXLSanNarOTUltra text-white font-black leading-[0.9] uppercase text-[16px] mb-[16px] mobileS:text-[20px] tablet:text-[24px] laptop:text-[26px] desktop:text-[30px] desktopLg:text-[37px]">
        {@html $copy.thankYou.subtitle}
      </h2>
      <div class="max-w-[90vw]">
        <a href="/" on:click|preventDefault={() => isBoxOpen = true}>
          <div class="prize-box flex justify-center tabletLg:ml-[-5%] ml-[0]" class:is-open={isBoxOpen}> <!-- [CHANGES-SM] added flex, justify-center -->
            <div class="prize-box__content delay-300 w-[425px] h-[252px] mt-[84px] tablet:w-[608px] tablet:h-[360px] tablet:mt-[152px]">
              <div class="prize-box__empty relative flex justify-center w-[425px] h-[252px] tablet:w-[608px] tablet:h-[360px]">
                <img src={EmptyBox} alt="">
                <div class="prize-box__light absolute w-[151%] left-[-27%] opacity-0 top-[-120%] z-[2]">
                  <img src={BoxLight} alt="">
                </div>
                <div class="prize-box__win absolute w-[90%] opacity-0 invisible bottom-[44%] left-[8%] z-[2]">
                  <img src={prizeAsset} alt="">
                </div>
              </div>
              <div class="prize-box__actions relative w-full h-full">
                <div class="prize-box__top absolute w-full h-full bg-transparent top-[-36%] tablet:top-[-60%]">
                  <div class="upper-part absolute w-full h-full top-[-48%]">
                    <div class="upper-part__inner absolute w-full h-full">
                      <div class="upper-part__front absolute w-full h-full opacity-100"></div>
                      <div class="upper-part__back absolute w-[109%] h-full top-[-7%] left-[-3%] opacity-0"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </a>
      </div>
      <div class="mt-[-48px]">
        {#if isPrimaryCampaign}
          <div class="font-MrEavesXLSanNarOT text-[12px] mb-[8px] text-white font-normal leading-[0.75] mobile:text-[14px] laptop:text-[15px] desktop:text-[16px] desktopLg:text-[18px]">
            {$copy.thankYou.prizeInfo}
          </div>
        {/if}
        {#if isPrimaryCampaign || !isOperatorSkin}
          <h2 class="font-MrEavesXLSanNarOTUltra text-white font-black leading-[0.9] uppercase text-[16px] mb-[32px] mobileS:text-[20px] tablet:text-[24px] laptop:text-[26px] desktop:text-[30px] desktopLg:text-[37px]">
            {@html $copy.thankYou.orderAgain}
          </h2>
        {/if}
        {#if isPrimaryCampaign}
          <button
            on:click={() => storeSelector($countryStore)}
            class="std-button font-MrEavesXLSanNarOTUltra relative inline-flex justify-center items-center text-[16px] whitespace-nowrap text-white px-[12px] uppercase h-[42px] min-w-[132px] bg-dark border-solid border-white border-2 no-underline uppercase transition cursor-pointer overflow-hidden tablet:text-[18px] tablet:h-[48px]">
            <span class="relative z-[1]">
              {@html $copy.header.menus.orderFromLittleCaesars}
            </span>
          </button>
        {/if}
      </div>
    </div>
  </div>
</main>
